import React, { useState } from "react";
import { startRegistration, isWebAuthnSupported } from "../auth/webauthn";
import { COGNITO } from "../config";
import { getValidAccessToken } from "../auth/client";

interface PasskeyEnrollProps {
  /** Called after successful enrollment */
  onEnrolled: () => void;
  /** Called if user wants to skip/cancel */
  onCancel?: () => void;
}

/**
 * Passkey enrollment UI.
 *
 * Shown when a user has no registered passkeys.
 * Guides the user through creating their first passkey:
 * 1. Click "Set up passkey"
 * 2. Browser prompts for biometric / shows QR code for phone
 * 3. Credential is registered with the FIDO2 API
 */
export const PasskeyEnroll: React.FC<PasskeyEnrollProps> = ({
  onEnrolled,
  onCancel,
}) => {
  const [status, setStatus] = useState<"idle" | "registering" | "success" | "error">("idle");
  const [error, setError] = useState("");
  const [friendlyName, setFriendlyName] = useState("");

  const handleEnroll = async () => {
    if (!isWebAuthnSupported()) {
      setError("WebAuthn is not supported in this browser.");
      setStatus("error");
      return;
    }

    setStatus("registering");
    setError("");

    try {
      // Step 1: Get registration options from server
      // Same token source as every other API call (sessionStorage
      // "tj.tokens.v1", refreshed when near expiry).
      const accessToken = await getValidAccessToken();

      if (!accessToken) {
        throw new Error("Not authenticated. Please sign in first.");
      }

      const beginResp = await fetch(`${COGNITO.apiBase}/fido2/register-begin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!beginResp.ok) {
        const errBody = await beginResp.json().catch(() => ({}));
        throw new Error(errBody.error || "Failed to start registration");
      }

      const { options } = await beginResp.json();

      // Step 2: Create credential via browser WebAuthn API
      const credential = await startRegistration(options);

      // Step 3: Complete registration on server
      const completeResp = await fetch(`${COGNITO.apiBase}/fido2/register-complete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          credential,
          friendly_name: friendlyName || "My Passkey",
        }),
      });

      if (!completeResp.ok) {
        const errBody = await completeResp.json().catch(() => ({}));
        throw new Error(errBody.error || "Failed to complete registration");
      }

      setStatus("success");
      setTimeout(() => onEnrolled(), 1500);
    } catch (err: any) {
      setError(err.message || "Registration failed");
      setStatus("error");
    }
  };

  return (
    <div className="enroll-container">
      <div className="enroll-card">
        <div className="enroll-icon">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" style={{ stroke: "var(--color-brand)" }} strokeWidth="1.5">
            <path d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
          </svg>
        </div>

        {status === "idle" && (
          <>
            <h2>Set up your passkey</h2>
            <p>
              Passkeys let you sign in securely using your fingerprint, face,
              or by scanning a QR code with your phone. No codes to remember.
            </p>

            <div className="enroll-field">
              <label htmlFor="passkey-name">Name this passkey (optional)</label>
              <input
                id="passkey-name"
                type="text"
                value={friendlyName}
                onChange={(e) => setFriendlyName(e.target.value)}
                placeholder='e.g., "My iPhone" or "Work Laptop"'
              />
            </div>

            <button className="enroll-submit" onClick={handleEnroll}>
              Set up passkey
            </button>

            {onCancel && (
              <button className="enroll-skip" onClick={onCancel}>
                Skip for now
              </button>
            )}
          </>
        )}

        {status === "registering" && (
          <>
            <h2>Creating your passkey...</h2>
            <p>
              Follow your browser's prompt to create a passkey.
              You may be asked to use Face ID, fingerprint, or scan a QR code.
            </p>
            <div className="enroll-spinner" />
          </>
        )}

        {status === "success" && (
          <>
            <div className="enroll-icon-success">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" style={{ stroke: "var(--color-success)" }} strokeWidth="2">
                <path d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2>Passkey created!</h2>
            <p>You can now use this passkey to sign in securely.</p>
          </>
        )}

        {status === "error" && (
          <>
            <h2>Setup failed</h2>
            <p className="enroll-error-msg">{error}</p>
            <button className="enroll-submit" onClick={handleEnroll}>
              Try again
            </button>
            {onCancel && (
              <button className="enroll-skip" onClick={onCancel}>
                Cancel
              </button>
            )}
          </>
        )}
      </div>

      <style>{`
        .enroll-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(145deg, var(--color-surface) 0%, var(--color-bg) 50%, var(--color-accent-ghost) 100%);
          padding: 20px;
        }
        .enroll-card {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
          padding: 48px 40px;
          width: 100%;
          max-width: 420px;
          text-align: center;
        }
        .enroll-icon, .enroll-icon-success {
          margin-bottom: 20px;
        }
        .enroll-card h2 {
          font-size: 22px;
          font-weight: 700;
          color: var(--color-title);
          margin: 0 0 12px 0;
        }
        .enroll-card p {
          font-size: 14px;
          color: var(--color-muted);
          margin: 0 0 24px 0;
          line-height: 1.5;
        }
        .enroll-field {
          text-align: left;
          margin-bottom: 20px;
        }
        .enroll-field label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: var(--color-title);
          margin-bottom: 6px;
        }
        .enroll-field input {
          width: 100%;
          padding: 10px 14px;
          border: 1px solid var(--color-border);
          border-radius: 8px;
          font-size: 14px;
          outline: none;
          box-sizing: border-box;
        }
        .enroll-field input:focus {
          border-color: var(--color-border-focus);
          box-shadow: var(--shadow-focus);
        }
        .enroll-submit {
          width: 100%;
          padding: 12px 24px;
          background-color: var(--color-brand);
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          margin-bottom: 12px;
          transition: background-color 0.15s;
        }
        .enroll-submit:hover {
          background-color: var(--color-brand-hover);
        }
        .enroll-skip {
          background: none;
          border: none;
          color: var(--color-muted);
          font-size: 13px;
          cursor: pointer;
          text-decoration: underline;
        }
        .enroll-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid var(--color-border);
          border-top-color: var(--color-brand);
          border-radius: 50%;
          animation: enroll-spin 0.8s linear infinite;
          margin: 0 auto;
        }
        @keyframes enroll-spin {
          to { transform: rotate(360deg); }
        }
        .enroll-error-msg {
          color: var(--color-danger) !important;
        }
      `}</style>
    </div>
  );
};
